/** Admin Control Room tab — manage driver benefits & partner discounts. */
import { useEffect, useRef, useState } from "react";
import { Gift, Pencil, Trash2, Plus, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY = { partner_name: "", discount_label: "", details: "", instructions: "", website_url: "", phone: "", logo_b64: null, active: true };
const INPUT = "w-full bg-black/40 border border-white/10 px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#C5A059]/60";

export const BenefitsTab = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const logoRef = useRef(null);

  const load = () => api.get("/admin/benefits").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickLogo = (file) => {
    if (!file) return;
    if (file.size > 250_000) return toast.error("Logo smije biti najviše 250 KB.");
    const r = new FileReader();
    r.onload = () => setForm((f) => ({ ...f, logo_b64: r.result }));
    r.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.partner_name.trim() || !form.details.trim()) return toast.error("Unesite partnera i opis pogodnosti.");
    setBusy(true);
    try {
      if (editId) await api.patch(`/admin/benefits/${editId}`, form);
      else await api.post("/admin/benefits", form);
      toast.success(editId ? "Pogodnost ažurirana." : "Pogodnost dodana — vozači je odmah vide.");
      setForm(EMPTY); setEditId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spremanje nije uspjelo.");
    } finally { setBusy(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Obrisati pogodnost "${b.partner_name}"?`)) return;
    try {
      await api.delete(`/admin/benefits/${b.id}`);
      toast.success("Pogodnost obrisana.");
      load();
    } catch { toast.error("Brisanje nije uspjelo."); }
  };

  return (
    <div data-testid="benefits-tab">
      <div className="flex items-center gap-2 mb-4">
        <Gift className="w-4 h-4 text-[#C5A059]" />
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Pogodnosti za vozače</p>
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0d0d0d] p-4 mb-5 grid gap-3 md:grid-cols-2">
        <input value={form.partner_name} onChange={set("partner_name")} placeholder="Partner (npr. Inter Cars)" className={INPUT} data-testid="benefit-partner" />
        <input value={form.discount_label} onChange={set("discount_label")} placeholder="Badge (npr. -40%)" className={INPUT} data-testid="benefit-discount" />
        <input value={form.details} onChange={set("details")} placeholder="Opis (npr. Do 40% popusta na auto dijelove)" className={`${INPUT} md:col-span-2`} data-testid="benefit-details" />
        <textarea value={form.instructions} onChange={set("instructions")} rows={2}
                  placeholder="Upute za korištenje (npr. Pozovite se na OIB tvrtke na blagajni)"
                  className={`${INPUT} md:col-span-2 resize-none`} data-testid="benefit-instructions" />
        <input value={form.website_url} onChange={set("website_url")} placeholder="Web stranica (npr. https://intercars.hr) — opcionalno" className={INPUT} data-testid="benefit-website" />
        <input value={form.phone} onChange={set("phone")} placeholder="Telefon partnera (npr. +385 1 234 5678) — opcionalno" className={INPUT} data-testid="benefit-phone" />
        <div className="flex items-center gap-3 md:col-span-2">
          <button type="button" onClick={() => logoRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 text-xs text-zinc-300 hover:bg-white/10 transition"
                  data-testid="benefit-logo-btn">
            <ImagePlus className="w-4 h-4" /> {form.logo_b64 ? "Promijeni logo" : "Dodaj logo (opcionalno)"}
          </button>
          {form.logo_b64 && <img src={form.logo_b64} alt="logo" className="h-9 w-9 rounded-lg object-contain bg-white/5 border border-white/10" />}
          <input ref={logoRef} type="file" accept="image/*" className="hidden"
                 onChange={(e) => pickLogo(e.target.files?.[0])} />
          <div className="ml-auto flex gap-2">
            {editId && (
              <button onClick={() => { setForm(EMPTY); setEditId(null); }}
                      className="px-4 py-2 rounded-full border border-white/15 text-xs text-zinc-400 hover:bg-white/10 transition">
                Odustani
              </button>
            )}
            <button onClick={save} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#C5A059] text-black text-xs font-semibold hover:bg-[#D4AF37] disabled:opacity-50 transition"
                    data-testid="benefit-save">
              <Plus className="w-3.5 h-3.5" /> {busy ? "Spremanje…" : editId ? "Spremi izmjene" : "Dodaj pogodnost"}
            </button>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">Još nema pogodnosti — dodajte prvu iznad.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {rows.map((b) => (
            <div key={b.id} className={`rounded-xl border p-4 flex gap-3 ${b.active ? "border-white/10 bg-[#0d0d0d]" : "border-white/5 bg-black/40 opacity-60"}`}
                 data-testid={`benefit-row-${b.id}`}>
              {b.logo_b64
                ? <img src={b.logo_b64} alt={b.partner_name} className="w-11 h-11 rounded-lg object-contain bg-white/5 border border-white/10 shrink-0" />
                : <div className="w-11 h-11 rounded-lg bg-[#C5A059]/10 border border-[#C5A059]/30 text-[#C5A059] flex items-center justify-center font-semibold shrink-0">
                    {b.partner_name?.[0]}
                  </div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-white truncate">{b.partner_name}</p>
                  {b.discount_label && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#C5A059] text-black">{b.discount_label}</span>
                  )}
                </div>
                <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{b.details}</p>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => { setForm({ ...EMPTY, ...b }); setEditId(b.id); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-[#C5A059] hover:bg-white/5 transition" title="Uredi"
                        data-testid={`benefit-edit-${b.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(b)}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-white/5 transition" title="Obriši"
                        data-testid={`benefit-delete-${b.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
